import {
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  EnumTagSuggestionEntityTypes,
} from "gql/generated";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { z } from "zod";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Adminland]);

const TagInfoEntityTypeSchema = z.union([
  z.nativeEnum(EnumTagSuggestionEntityTypes),
  z.nativeEnum(EnumTagEntityTypes),
]);

export const tagInfoFormSchema = z.object({
  id: z.string().min(1),
  tagGroup: z
    .string()
    .trim()
    .min(
      1,
      i18n.t(
        "adminland:PAGE.TAG_MANAGER.FLYOUTS.COMMON_FORM.ERRORS.TAG_GROUP_REQUIRED",
      ),
    ),
  name: z
    .string()
    .trim()
    .min(
      1,
      i18n.t(
        "adminland:PAGE.TAG_MANAGER.FLYOUTS.COMMON_FORM.ERRORS.NAME_REQUIRED",
      ),
    ),
  entityTypes: z.array(TagInfoEntityTypeSchema),
  inheritable: z.boolean().optional(),
});

export type TagInfoEntityTypeSchemaValues = z.infer<
  typeof TagInfoEntityTypeSchema
>;
export type TagInfoFormValues = z.infer<typeof tagInfoFormSchema>;

/**
 * Get the entity types for a tag group
 * - The entity types are based on the tag group type and key
 * @param tagGroupType
 * @param tagGroupKey
 * @returns
 */
export const getTagEntityTypesForTagGroup = (
  tagGroupType: EnumTagGroupType,
  tagGroupKey: EnumTagGroupKey,
) => {
  switch (tagGroupType) {
    case EnumTagGroupType.Amenity:
      if (
        tagGroupKey === EnumTagGroupKey.BedroomSpecific ||
        tagGroupKey === EnumTagGroupKey.RoofedAccommodation
      ) {
        return [
          EnumTagSuggestionEntityTypes.Operator,
          EnumTagSuggestionEntityTypes.Product,
        ];
      }
      return [EnumTagSuggestionEntityTypes.Operator];
    case EnumTagGroupType.BusinessType:
    case EnumTagGroupType.Global:
      return [EnumTagSuggestionEntityTypes.Operator];
    case EnumTagGroupType.GlobalEvent:
      return [EnumTagSuggestionEntityTypes.Event];
    case EnumTagGroupType.OrganizationSpecific:
      if (tagGroupKey === EnumTagGroupKey.BackOffice) {
        return [
          EnumTagSuggestionEntityTypes.Operator,
          EnumTagSuggestionEntityTypes.Event,
          EnumTagSuggestionEntityTypes.Person,
        ];
      }
      if (tagGroupKey === EnumTagGroupKey.MediaGallery) {
        return [EnumTagSuggestionEntityTypes.Organization];
      }
      return [];
  }
  return [];
};
